import { MeetingSourceElement, useMeetingSessionOvenMediaReceiverManager } from "@kalyzee/kast-web-sdk";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import {
  MeetingSession,
  MeetingSessionMedia,
  MeetingState
} from "@kalyzee/kast-web-sdk/websocket";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SlaveChat from "../../../components/chat/SlaveChat";
import { BaseTopBar } from "../../../components/common/BaseTopBar";
import { PageSlaveMode } from "../../../components/navigation/PageContext";
import PauseView from "../../../components/utils/PauseView";
import { ChatWorkBox } from "../../../components/workBoxes/ChatWorkBox";
import { PlayerItem } from "../../../components/player/interfaces";
import { PlayerGridWorkBox, PlayerGridWorkBoxProps } from "../../../components/workBoxes/GridWorkBox";
import { ScreenConstants } from "../../../constants/screens";
import { ClientContainersIteration } from "../../../helpers/bridge/common";
import { matchEngine } from "../../../helpers/matchEngine";
import { ScreenHelpers } from "../../../helpers/screen";
import { useMeetingSlaveMessagingBridge } from "../../../hooks/secondaryScreens/messagingBridge";
import { useScreen } from "../../../hooks/secondaryScreens/screen";
import { MeetingSourceExtended, useMeetingSources } from "../../../hooks/meeting";
import { useSettings } from "../../../hooks/settings";
import { SecondaryScreenLayoutVariant } from "../../../interfaces/secondaryScreens";
import styles from "./meeting.module.css";

const MeetingSlavePage = () => {
  const { bridge, meeting, session, sessions, slaveChatAdapter } = useMeetingSlaveMessagingBridge();
  const bridgeRef = useRefAutoUpdate(bridge);

  const { screen, layout } = useScreen();
  const [settings] = useSettings();

  // ---------------------------------------------------------------------- //
  // ---------------------------   STATE ---------------------------------- //
  // ---------------------------------------------------------------------- //

  const [iteration, setIteration] = useState<ClientContainersIteration>({ containers: [] });
  const [pendingIteration, setPendingIteration] = useState<ClientContainersIteration | null>(null);
  const [localStreams, setLocalStreams] = useState<Record<number, MediaStream>>({});

  const timeoutDisplaySessionsRef = useRef<NodeJS.Timeout>();

  // ---------------------------------------------------------------------- //
  // ---------------------------   MEMOS ---------------------------------- //
  // ---------------------------------------------------------------------- //

  const layoutVariant = useMemo(() => {
    return layout.variants?.find(v => {
      if (!v.match) return true;
      return matchEngine(
        { meeting, currentSession: session, videoContainers: iteration.containers },
        v.match,
      );
    });
  }, [layout, meeting, session, iteration.containers]);

  // --- Local sources: detect which sources the slave can load locally ---

  const sources = useMeetingSources();

  /** Maps sourceIndex → { source config, mediaIds using it } for current + pending iterations */
  const neededSources = useMemo(() => {
    const map = new Map<number, { source: MeetingSourceExtended, mediaIds: string[] }>();
    const processContainers = (containers: ClientContainersIteration["containers"]) => {
      for (const container of containers) {
        if (!container.sourceIndexes) continue;
        for (const [mediaId, sourceIndex] of Object.entries(container.sourceIndexes)) {
          const source = sources[sourceIndex];
          if (source) {
            const entry = map.get(sourceIndex) ?? { source, mediaIds: [] };
            if (!entry.mediaIds.includes(mediaId)) entry.mediaIds.push(mediaId);
            map.set(sourceIndex, entry);
          }
        }
      }
    };
    processContainers(iteration.containers);
    if (pendingIteration) processContainers(pendingIteration.containers);
    return map;
  }, [iteration, pendingIteration, sources]);

  /** Set of media IDs that have a local source — skip OvenMedia entirely for these */
  const localMediaIds = useMemo(() => {
    const ids = new Set<string>();
    Array.from(neededSources.values()).forEach(({ mediaIds }) => {
      mediaIds.forEach((id: string) => ids.add(id));
    });
    return ids;
  }, [neededSources]);

  const { manager: receptionManager, sessions: videoSessions } = useMeetingSessionOvenMediaReceiverManager(
    sessions,
    useCallback(
      (_curr: MeetingSession, s: MeetingSessionMedia) => {
        if (localMediaIds.has(s.id)) return false;
        if (pendingIteration?.containers.some(c => c.sessions.some(cs => cs.id === s.id))) return true;
        return iteration.containers.some(c => c.sessions.some(cs => cs.id === s.id));
      },
      [iteration, pendingIteration, localMediaIds]
    ),
  );

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ---------------------------------- //
  // ---------------------------------------------------------------------- //

  const clean = () => {
    bridgeRef.current?.postClearClientData(PageSlaveMode.MEETING);
    clearTimeout(timeoutDisplaySessionsRef.current);
  };

  /** Resolve fresh session/media from bridge data (carousel snapshots can be stale) */
  const resolveFreshMedia = (mediaId: string) => {
    if (!sessions) return undefined;
    for (const s of sessions) {
      const m = s.medias?.find(m => m.id === mediaId);
      if (m) return { session: s, media: m };
    }
    return undefined;
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECTS -------------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    bridgeRef.current?.postClientData(PageSlaveMode.MEETING, {
      layoutId: layout.id,
    });
  }, [screen, meeting, layout, bridgeRef]);

  useEffect(() => {
    if (meeting?.state === MeetingState.RUNNING) {
      receptionManager.restore();
    } else {
      receptionManager.pause();
    }
  }, [meeting, receptionManager]);

  useEffect(() => {
    if (!bridge) return;
    const displayCarouselListener = bridge.addEventListener("displayCarousel", (msg) => {
      const { data, delay } = msg.content;
      if (delay <= 0) {
        clearTimeout(timeoutDisplaySessionsRef.current);
        setPendingIteration(null);
        setIteration(data);
      } else {
        setPendingIteration(data);
        timeoutDisplaySessionsRef.current = setTimeout(() => {
          setPendingIteration(null);
          setIteration(data);
        }, delay);
      }
    });
    return () => {
      displayCarouselListener.flush();
      clearTimeout(timeoutDisplaySessionsRef.current);
    };
  }, [bridge]);

  useEffect(() => {
    return () => { clean(); };
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  // --- Invisible local sources ---

  const renderInvisibleSources = () =>
    Array.from(neededSources.entries()).map(([sourceIndex, { source }]) => (
      <MeetingSourceElement
        key={`local-source-${sourceIndex}`}
        source={source}
        using={true}
        className={styles.invisibleSource}
        callbacks={{
          track: (_track, data) => {
            if (data.media) setLocalStreams(prev => ({ ...prev, [sourceIndex]: data.media! }));
          },
          update: (data) => {
            if (data.media) setLocalStreams(prev => ({ ...prev, [sourceIndex]: data.media! }));
          },
          destroy: () => {
            setLocalStreams(prev => {
              const next = { ...prev };
              delete next[sourceIndex];
              return next;
            });
          },
        }}
      />
    ));

  // --- Debug ---

  const renderMediaDebugLine = (media: MeetingSessionMedia, sourceIndexes: Record<string, number> | undefined, status: "displayed" | "preloaded") => {
    const sourceIndex = sourceIndexes?.[media.id];
    const isLocal = sourceIndex !== undefined;
    const statusColor = status === "preloaded" ? "#ff9800" : "#4caf50";
    return (
      <div key={`${media.id}-${status}`} style={{ fontSize: "0.8em" }}>
        <span style={{ color: "#4caf50" }}>[{media.id.slice(0, 8)}]</span>
        {' : '}
        <span>{isLocal ? `localstream=src[${sourceIndex}]` : 'ovenmedia'}</span>
        {' '}
        <span style={{ color: statusColor }}>{status}</span>
      </div>
    );
  };

  const renderIterationDebugData = (iter: ClientContainersIteration, status: "displayed" | "preloaded") =>
    iter.containers.flatMap(container =>
      container.sessions.map(media => renderMediaDebugLine(media, container.sourceIndexes, status))
    );

  const renderGlobalDebug = () => (
    <div className={styles.globalDebugContainer}>
      <div>Bridge : <span style={{ color: bridge ? "#4caf50" : "#f44336" }}>{bridge ? 'connected' : 'disconnected'}</span></div>
      <div>Meeting : <span style={{ color: meeting ? "#4caf50" : "#f44336" }}>{meeting ? `${meeting.id?.slice(0, 8)} (${meeting.state})` : 'null'}</span></div>
      <div>Session : <span style={{ color: session ? "#4caf50" : "#f44336" }}>{session ? session.id?.slice(0, 8) : 'null'}</span></div>
      <div>Sessions : {sessions?.length ?? 0}</div>
      <div>Variant : {JSON.stringify(layoutVariant)}</div>
      <div style={{ marginTop: 8 }}>
        {renderIterationDebugData(iteration, "displayed")}
        {pendingIteration && renderIterationDebugData(pendingIteration, "preloaded")}
      </div>
    </div>
  );

  // --- Top bar ---

  const renderTopBar = () => {
    const topBarOptions = layoutVariant?.topBar;
    if (!topBarOptions) return null;
    return (
      <BaseTopBar
        showLogo={topBarOptions.brand}
        watchingNumber={topBarOptions.watchers ? sessions?.length : undefined}
        user={topBarOptions.user ? session?.user : undefined}
        meetingName={topBarOptions.meetingName ? meeting?.name : undefined}
        meetingStartedAt={topBarOptions.timer ? meeting?.startedAt : undefined}
      />
    );
  };

  // --- Pager ---

  const renderPagerDots = (page: number, pageCount: number) =>
    new Array(pageCount).fill(0).map((_, i) => (
      <div
        key={`page_${i}`}
        className={styles.pagerDot}
        style={{ backgroundColor: i === page ? "#CCCCCCDD" : "#AAAAAA44" }}
      />
    ));

  const renderPager = (page: number, pageCount: number) => (
    pageCount > 1 ? (
      <div className={styles.pager}>{renderPagerDots(page, pageCount)}</div>
    ) : null
  );

  const renderPagerOverlay = (page: number, pageCount: number) => (
    pageCount > 1 ? (
      <div className={clsx(styles.pager, styles.pagerOverlay)}>{renderPagerDots(page, pageCount)}</div>
    ) : null
  );

  // --- Video / Chat columns ---

  const renderVideoColumn = (
    cols: number,
    rows: number,
    render: (children: React.ReactNode) => React.ReactNode,
    container?: ClientContainersIteration["containers"][0],
    boxPreset?: PlayerGridWorkBoxProps["preset"],
    playerStyle?: React.CSSProperties,
    overlayMode?: "normal" | "overlay" | "overlay-hoverable",
    singlePlayerMode?: boolean,
  ) => {
    if (!meeting || !session || !container) return null;

    const columnItems: PlayerItem[] = container.sessions
      .map((media): PlayerItem | null => {
        const fresh = resolveFreshMedia(media.id);
        const freshMedia = fresh?.media ?? media;
        const freshSession = fresh?.session;

        // Try local stream first
        const sourceIndex = container.sourceIndexes?.[media.id];
        const localStream = sourceIndex !== undefined ? localStreams[sourceIndex] : undefined;

        if (localStream) {
          return new PlayerItem({
            type: "stream",
            mediaStream: localStream,
            extra: { meetingSession: freshSession, mediaSession: freshMedia },
          });
        }

        // Fallback to OvenMedia
        const videoSession = videoSessions.find(s => s.data.mediaSession.id === media.id);
        if (!videoSession) return null;

        return new PlayerItem({
          type: "video_session",
          videoSession,
          extra: { meetingSession: freshSession ?? videoSession.data.session, mediaSession: freshMedia },
        });
      })
      .filter((item): item is PlayerItem => !!item);

    if (!columnItems.length) return null;

    const effectiveCols = singlePlayerMode ? 1 : cols;
    const effectiveRows = singlePlayerMode ? 1 : rows;

    return render(
      <div className={styles.column}>
        <PlayerGridWorkBox
          meeting={meeting}
          sessions={sessions ?? []}
          ownSession={session}
          items={columnItems}
          showLocalStreamBadge={!!settings.carousel?.debug}
          columns={effectiveCols}
          rows={effectiveRows}
          preset={boxPreset}
          gap={5}
          totalNumber={container.totalSessions}
          playerStyle={playerStyle}
          overlayMode={overlayMode}
        />
        {settings.carousel?.debug && (
          <div className={styles.debugContainer}>
            <div>Carousel ID : {container.carouselId}</div>
            <div>Group ID : {container.groupId}</div>
            <div>Page : {container.page + 1} / {container.pageCount}</div>
          </div>
        )}
        {singlePlayerMode
          ? renderPagerOverlay(container.page, container.pageCount)
          : renderPager(container.page, container.pageCount)
        }
      </div>
    );
  };

  const renderChatColumn = () => (
    <ChatWorkBox>
      <SlaveChat chatAdapter={slaveChatAdapter} />
    </ChatWorkBox>
  );

  // --- Layout ---

  const renderLayoutColumns = () => {
    const nodes: React.ReactNode[] = [];

    const renderColumn = (
      column: SecondaryScreenLayoutVariant["columns"][0],
      render: (children: React.ReactNode) => React.ReactNode,
    ) => {
      if (column.contentType === "players") {
        const dim = ScreenHelpers.getColumnPlayers(column);
        const container =
          layout.videoContainers?.find(c => c.id === column.videoContainerId)
          ?? ScreenConstants.DefaultLayout.videoContainers?.[0];
        const containerIteration = iteration.containers.find(c => c.id === container?.id);
        return renderVideoColumn(dim.columns, dim.rows, render, containerIteration, column.playerBoxPreset, column.playerStyle, column.overlayMode, column.singlePlayerMode);
      } else if (column.contentType === "chat") {
        return render(renderChatColumn());
      } else {
        return render(<>Unimplemented column type</>);
      }
    };

    layoutVariant?.columns.forEach((column, idx) => {
      const _render = (children: React.ReactNode) => (
        <div
          style={{ height: "100%", width: "25%", ...(column.style ?? {}) }}
          key={`column_${column.contentType === "chat" ? "chat" : `players_${idx}`}`}
        >
          {children}
        </div>
      );
      nodes.push(renderColumn(column, _render));
    });

    return (
      <div style={{
        height: "100%", width: "100%", display: "flex",
        gap: "1em", justifyContent: layoutVariant?.justifyContent,
      }}>
        {nodes}
      </div>
    );
  };

  // --- Content ---

  const renderContent = () => {
    if (!meeting || !session) {
      const screenName = screen?.name ?? (screen?.index !== undefined ? `Écran n°${screen.index}` : undefined);
      return (
        <div className={styles.waiting}>
          <div className={styles.content}>En attente d'un événement ...</div>
          {screenName && <div className={styles.footer}>{screenName}</div>}
        </div>
      );
    }
    return (
      <div className={styles.layoutWrapper}>
        {renderLayoutColumns()}
      </div>
    );
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   MAIN RETURN ---------------------------- //
  // ---------------------------------------------------------------------- //

  return (
    <>
      {renderInvisibleSources()}
      <PauseView
        meetingPaused={meeting?.state === MeetingState.PAUSED}
        meetingPausedAt={meeting?.pausedAt}
      >
        <div className={styles.container}>
          {renderTopBar()}
          <div className={styles.contentWrapper} style={layoutVariant?.layoutStyle}>
            {renderContent()}
          </div>
          {settings.carousel?.debug && renderGlobalDebug()}
        </div>
      </PauseView>
    </>
  );
};

export default MeetingSlavePage;
